import type { FC } from 'react'

import { Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import { usePolicyContext } from '@/components/ecom-policy/policy/Policy.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { ecomPolicyResources } from '@/resources/ecom-policy'
import { APP_ROUTES } from '@/util/appRoutes'
import { scrollToTop } from '@/util/misc'

export const SummaryButtons: FC<{
  isOnlyAccessoriesPolicy: boolean
  isAccessoriesVisible: boolean
  isSpendingLimitExceeded: boolean
  isGreenpoolAvailable: boolean
}> = ({
  isAccessoriesVisible,
  isOnlyAccessoriesPolicy,
  isSpendingLimitExceeded,
  isGreenpoolAvailable,
}) => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const [querySearch, setSearchQuery] = useSearchParamsByKey('search')

  const policyId = usePolicyId()
  const policyCtx = usePolicyContext()
  const [activeStep, setActiveStep] = policyCtx.activeStep
  const [selectedProductIds] = policyCtx.selectedProductIds
  const [selectedAccessoriesIds] = policyCtx.selectedAccessoriesIds
  const [selectedGreenpoolProductId] = policyCtx.selectedGreenpoolProductId

  const { data } = useQuery(queries.policy(policyId), () => ecomPolicyResources.get(policyId))
  const isOnlyProductView = Boolean(data?.isLocked && data?.isProductViewAllowedWhenLocked)

  const hasSelectedProducts = Boolean(
    (selectedProductIds && selectedProductIds?.length > 0) ||
      (selectedAccessoriesIds && selectedAccessoriesIds?.length > 0)
  )
  const isGreenpoolOrderReady = data?.greenpoolTypeId === 'MANDATORY' || selectedGreenpoolProductId
  const isBackToGreenpoolButtonVisible =
    activeStep === 'PRODUCT_SELECT' && isGreenpoolAvailable && !selectedGreenpoolProductId

  const clickNextForGreenpoolProductSelect = () => {
    scrollToTop()
    if (querySearch) {
      setSearchQuery('')
    }

    if (selectedGreenpoolProductId && !isOnlyProductView) {
      navigate(APP_ROUTES.cart)
    } else {
      const nextStep = isOnlyAccessoriesPolicy ? 'ACCESSORIES' : 'PRODUCT_SELECT'
      setActiveStep(nextStep)
      return
    }
  }

  const clickNext = async () => {
    scrollToTop()
    if (querySearch) {
      setSearchQuery('')
    }

    if (activeStep === 'PRODUCT_SELECT' && isAccessoriesVisible) {
      setActiveStep('ACCESSORIES')
      return
    } else if (!isOnlyProductView) {
      navigate(APP_ROUTES.cart)
    }
  }

  const clickBack = () => {
    scrollToTop()
    if (querySearch) {
      setSearchQuery('')
    }

    if (
      activeStep === 'GREENPOOL_PRODUCT_SELECT' &&
      data?.greenpoolTypeId === 'OPTIONAL' &&
      !selectedGreenpoolProductId
    ) {
      setActiveStep('GREENPOOL_INFO')
      return
    }

    if (activeStep === 'ACCESSORIES') {
      const prevGreenpoolStepAvailable =
        isGreenpoolAvailable && isOnlyAccessoriesPolicy && !selectedGreenpoolProductId
      if (prevGreenpoolStepAvailable) {
        setActiveStep('GREENPOOL_PRODUCT_SELECT')
        return
      } else if (!isOnlyAccessoriesPolicy) {
        setActiveStep('PRODUCT_SELECT')
        return
      }
    }

    if (activeStep === 'PRODUCT_SELECT' && isGreenpoolAvailable && !hasSelectedProducts) {
      setActiveStep('GREENPOOL_INFO')
      return
    }

    navigate(APP_ROUTES.ecomPolicyList)
  }

  const isConfirmationNext = () => {
    const afterProductSelect = activeStep === 'PRODUCT_SELECT' && !isAccessoriesVisible
    const afterGreenpoolSelect =
      activeStep === 'GREENPOOL_PRODUCT_SELECT' && selectedGreenpoolProductId
    return afterProductSelect || afterGreenpoolSelect || !isAccessoriesVisible
  }

  return (
    <div
      className={twJoin(
        'flex flex-row items-center justify-between gap-8 max-md:mb-5 md:justify-start',
        isOnlyProductView && 'mt-2'
      )}
    >
      <Button onClick={clickBack} className="m-0 min-w-24">
        <Translation id="Back" className="capitalize" />
      </Button>

      {isBackToGreenpoolButtonVisible && (
        <span
          data-tooltip-id="choose-greenpool"
          data-tooltip-content={translate('Please deselect products first')}
        >
          <Button
            disabled={hasSelectedProducts}
            className="max-mobile:hidden"
            onClick={() => {
              scrollToTop()
              setActiveStep('GREENPOOL_PRODUCT_SELECT')
            }}
          >
            {isOnlyProductView ? (
              <Translation id="Go to Greenpool" />
            ) : (
              <Translation id="Choose Greenpool" />
            )}
          </Button>
        </span>
      )}
      {isBackToGreenpoolButtonVisible && hasSelectedProducts && (
        <Tooltip id="choose-greenpool" size="large" style={{ zIndex: 100 }} />
      )}

      {activeStep === 'GREENPOOL_PRODUCT_SELECT' && (
        <Button
          variant={isGreenpoolOrderReady ? 'primary' : 'secondary'}
          onClick={clickNextForGreenpoolProductSelect}
          disabled={
            !isOnlyProductView &&
            data?.greenpoolTypeId === 'MANDATORY' &&
            !selectedGreenpoolProductId
          }
        >
          <Translation
            id={isGreenpoolOrderReady && !isOnlyProductView ? 'To checkout' : 'Go to the products'}
          />
        </Button>
      )}

      {activeStep !== 'GREENPOOL_PRODUCT_SELECT' &&
        !(isOnlyProductView && isConfirmationNext()) && (
          <Button
            variant={hasSelectedProducts ? 'primary' : 'secondary'}
            className="m-0 min-w-24"
            onClick={clickNext}
            // There might appear accessories independent of the selected products
            disabled={
              Boolean(isOnlyProductView && isConfirmationNext()) ||
              (!selectedGreenpoolProductId &&
                ((!hasSelectedProducts && !isAccessoriesVisible) || isSpendingLimitExceeded))
            }
          >
            {isConfirmationNext() ? (
              <Translation id="To checkout" />
            ) : hasSelectedProducts ? (
              <Translation id="Proceed" className="capitalize" />
            ) : (
              <Translation id="Skip" className="capitalize" />
            )}
          </Button>
        )}
    </div>
  )
}
